import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPageV2.css';
import { t, useI18n } from '../../i18n';

const LandingPageV2 = ({ onGetStarted, onSignIn, onBrowseTemplates, isAuthenticated = false }) => {
  const { locale, setLocale } = useI18n();
  const [scrollY, setScrollY] = useState(0);
  const getInitialChatMessages = () => {
    const capabilitiesList = [
      t('Work as a live-chat with built-in AI on your website'),
      t('Answer using your knowledge base, FAQ and documentation'),
      t('Help operators handle several dialogues at once without losing context'),
      t('Collect leads in the chat (name, contacts, interest) and send them to CRM or by email'),
      /*t('Proactively start conversations with visitors based on rules and context'),*/
      t('Store and show full chat history in the dashboard'),
      t('Support several operators and AI-agent in one interface'),
      /*t('Provide analytics on dialogues and conversions')*/
    ]
      .map((item) => `• ${item}`)
      .join('\n');

    return [
      {
        id: 1,
        type: 'agent',
        text: t(
          'Supchats is a universal consultant.\nUse information from our website to describe capabilities, and documentation from the dashboard.'
        ),
        timestamp: Date.now() - 120000
      },
      {
        id: 2,
        type: 'user',
        text: t('What can you do?'),
        timestamp: Date.now() - 90000
      },
      {
        id: 3,
        type: 'agent',
        text: `${t('I can:\n')} ${capabilitiesList}`,
        timestamp: Date.now() - 70000
      }
    ];
  };

  const [chatMessages, setChatMessages] = useState(getInitialChatMessages);
  const chatMessagesRef = useRef(null);

  const handleLocaleToggle = () => {
    const newLocale = locale === 'ru' ? 'en' : 'ru';
    setLocale(newLocale);
  };

  // Лёгкий параллакс для hero-секции
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY || window.pageYOffset || 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Переключение языка: обновляем демо-диалог без перезагрузки страницы
  useEffect(() => {
    setChatMessages(getInitialChatMessages());
  }, [locale]);

  // Intersection Observer для анимаций при скролле (в обе стороны)
  useEffect(() => {
    const sections = document.querySelectorAll('.fade-in-section, .slide-in-section, .feature-card, .use-case-card, .comparison-row');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          } else {
            entry.target.classList.remove('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Анимированный таймер "3:00 → 0:00" с круговым прогрессом (реальные 3 минуты)
  const SetupTimer = () => {
    const TOTAL_SECONDS = 3 * 60;

    const [remaining, setRemaining] = useState(TOTAL_SECONDS);

    useEffect(() => {
      // При смене языка перезапускаем таймер
      setRemaining(TOTAL_SECONDS);

      const intervalId = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 0) {
            // начинаем новый цикл 3 минут
            return TOTAL_SECONDS;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(intervalId);
      };
    }, [locale]);

    const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
    const ss = String(remaining % 60).padStart(2, '0');

    const radius = 74;
    const circumference = 2 * Math.PI * radius;
    const progress = remaining / TOTAL_SECONDS; // 1..0
    const dashOffset = circumference * (1 - progress);

    return (
      <div className="setup-timer">
        <div className="setup-timer-main">
          <div className="setup-timer-circle">
            <svg
              className="setup-timer-svg"
              viewBox="0 0 200 200"
            >
              <defs>
                <linearGradient id="timerGradient" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#22c55e" />
                  <stop offset="50%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
              <g transform="rotate(-90 100 100)">
                <circle
                  className="setup-timer-track"
                  cx="100"
                  cy="100"
                  r={radius}
                />
                <circle
                  className="setup-timer-progress"
                  cx="100"
                  cy="100"
                  r={radius}
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                />
              </g>
            </svg>
            <div className="setup-timer-time">
              {mm}:{ss}
            </div>
          </div>
          <div className="setup-timer-text">
            <div className="setup-timer-title">
              {t('In this time you could already install Supchats on your website')}
            </div>
          </div>
        </div>
        <div className="setup-timer-steps">
          <span>{t('Sign up')}</span>
          <span>→</span>
          <span>{t('Create bot')}</span>
          <span>→</span>
          <span>{t('Add widget')}</span>
          <span>→</span>
          <span>{t('Enjoy AI support')}</span>
        </div>
      </div>
    );
  };

  const features = [
    {
      icon: '🎯',
      title: t('Ready-made prompt presets for different tasks'),
      description: t('Support, sales, lead qualification, warm pre-sales, FAQ — choose the communication style you need in one click.')
    },
    {
      icon: '📚',
      title: t('Unified knowledge base'),
      description: t('Upload FAQs, articles, instructions and internal regulations — AI and operators rely on one database when answering customers.')
    },
    {
      icon: '💬',
      title: t('Working with multiple dialogues at once'),
      description: t('Operators handle multiple chats in parallel without losing context and customer history.')
    },
    {
      icon: '📋',
      title: t('Lead collection and transfer'),
      description: t('The form in the chat collects name, contact details, product of interest. Leads can be exported or transferred to CRM through integrations / webhooks.')
    },
    {
      icon: '🔌',
      title: t('Integration with popular CMS and builders'),
      description: t('Connection via widget code, GTM or ready-made integrations with common CMS (WordPress, Tilda, etc.).')
    },
    {
      icon: '🛡️',
      title: t('GDPR and hosting in the EU'),
      description: t('Data is processed in accordance with European data protection legislation, infrastructure is located in the EU.')
    },
    {
      icon: '👥',
      title: t('Unlimited number of operators in the free plan'),
      description: t('Add the whole team: support, sales, marketing — no extra charge per user.')
    },
    {
      icon: '🚀',
      title: t('Proactive dialogues'),
      description: t('The widget itself initiates a conversation with the client based on the context of previous conversations or data transmitted by the system — offers help at the right moment and increases engagement.')
    }
  ];

  const comparisonItems = [
    {
      feature: t('24/7 no days off'),
      ai: t('AI doesn\'t go to lunch, doesn\'t get sick, doesn\'t "go off shift". No night surcharges, shift schedules or holiday replacements.'),
      operator: t('Work schedule, weekends, vacations, sick leave. The need to organize shifts.')
    },
    {
      feature: t('Scalability without hiring'),
      ai: t('10, 100 or 1000 dialogues at the same time — this is normal for AI. No need to urgently hire and train new operators during traffic spikes (advertising, promotions, season).'),
      operator: t('Limited by the number of operators. When the load increases, hiring and training are needed.')
    },
    {
      feature: t('Instant response speed'),
      ai: t('Answers in fractions of a second, without "operator will connect in 5 minutes". Strong reduction in bounces and site exits due to waiting.'),
      operator: t('Depends on operator workload. Waiting time can reach several minutes.')
    },
    {
      feature: t('Consistent quality and scenarios'),
      ai: t('AI always follows scripts/knowledge base: doesn\'t "improvise" like a tired operator. No human factor: bad mood, rudeness, fatigue, loss of concentration.'),
      operator: t('Quality depends on operator experience, mood and fatigue.')
    },
    {
      feature: t('Cheaper in the long run'),
      ai: t('No salary, bonuses, taxes, sick leave, vacations. You pay for usage/limits, not for time sitting "on the line".'),
      operator: t('Fixed salary + taxes + social package regardless of load.')
    },
    {
      feature: t('Easy to train and retrain'),
      ai: t('Updated product/promotion — just added to the knowledge base or prompts. No need to conduct 3–5 training calls with each new shift.'),
      operator: t('Time is required to train each employee when changes occur.')
    },
    {
      feature: t('Multilingual out of the box'),
      ai: t('AI can respond in multiple languages without a "staff translator". Convenient if advertising goes to several countries/regions.'),
      operator: t('Separate operators are needed for each language.')
    },
    {
      feature: t('Deep analytics and control'),
      ai: t('All dialogues are easy to analyze: types of questions, conversions, bottlenecks. You can build funnels: from which questions people most often reach an application/payment.'),
      operator: t('It\'s harder to collect unified analytics across all operators and dialogues.')
    },
    {
      feature: t('Strict compliance with regulations'),
      ai: t('AI will never make promises that are not in the offer, if it is strictly prescribed. Convenient for legally sensitive niches (finance, medicine, real estate).'),
      operator: t('Risk of human error and promises outside regulations.')
    },
    {
      feature: t('Doesn\'t burn out and doesn\'t "burn"'),
      ai: t('No emotional burnout, no need to deal with motivation and HR. Difficult or toxic clients don\'t spoil anyone\'s mood in the team.'),
      operator: t('Subject to emotional burnout, require HR support.')
    },
    {
      feature: t('Data collection and enrichment'),
      ai: t('AI can carefully ask clarifying questions and structure answers (name, contact, product of interest, budget, deadline). Further, all this can be sent to CRM or manager already in ready form.'),
      operator: t('Depends on operator discipline in filling out forms.')
    },
    {
      feature: t('Consistent style and tone of communication'),
      ai: t('Any client receives the same "brand" communication. Doesn\'t depend on which operator is on shift today.'),
      operator: t('Communication style differs between different operators.')
    },
    {
      feature: t('Fast A/B scenario switching'),
      ai: t('You can test different greetings, scripts, questions, dialogue branches without retraining people. And quickly see which logic converts better into application/payment.'),
      operator: t('Testing new scenarios requires training the entire team.')
    }
  ];

  const useCases = [
    {
      title: t('Online consultant on the site'),
      description: t('Tell Supchats about your services, rates, promotions and conditions. The widget on the site turns into a clear online consultant: answers frequent questions, helps choose a product and pushes to an application or purchase.'),
      cta: t('Connect online consultant')
    },
    {
      title: t('Support and knowledge base'),
      description: t('Upload your FAQs, instructions, knowledge base articles to Supchats. AI helps operators find the right answer faster, and in simple cases copes on its own — customers receive help 24/7, and the load on support decreases.'),
      cta: t('Set up support')
    },
    {
      title: t('Sales and lead qualification'),
      description: t('Use ready-made prompt presets for sales and qualification. The chat asks clarifying questions, collects contacts and transfers to the operator already "warmed up" leads ready to communicate with the manager.'),
      cta: t('Launch lead generation')
    }
  ];

  return (
    <div className="landing-v2">
      {/* Header */}
      <header className="landing-v2-header">
        <div className="header-container">
          <div className="logo">
            <img
              src={process.env.PUBLIC_URL + '/logo512.png'}
              alt="Supchats logo"
              className="logo-img"
            />
            <span className="logo-text">Supchats</span>
          </div>
          <nav className="nav-menu">
            <div className="language-switcher">
              <label className="language-toggle-label">
                <input
                  type="checkbox"
                  checked={locale === 'ru'}
                  onChange={handleLocaleToggle}
                  className="language-toggle-checkbox"
                />
                <span className="language-toggle-slider">
                  <span className="language-toggle-text">{locale === 'ru' ? 'RU' : 'EN'}</span>
                </span>
              </label>
            </div>
            {!isAuthenticated && (
              <>
                <Link to="/templates" className="nav-link-templates">
                  {t('Templates')}
                </Link>
                <button
                  className="btn-signup"
                  onClick={onGetStarted}
                >
                  {t('Sign Up')}
                </button>
                <button
                  className="btn-signin"
                  onClick={() => {
                    try { localStorage.setItem('auth_mode', 'login'); } catch (e) {}
                    onSignIn();
                  }}
                >
                  {t('Sign In')}
                </button>
              </>
            )}
            {isAuthenticated && (
              <button className="btn-signin" onClick={onSignIn}>
                {t('Dashboard')}
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div
            className="hero-content"
            style={{ transform: `translateY(${-(scrollY * 0.04)}px)` }}
          >
            <h1 className="hero-title">
              {t('Connect a smart online chat to your website in 2 minutes')}
            </h1>

            <SetupTimer />

            <p className="hero-description">
              {t('Supchats — an easy live-chat with built-in AI for your website. Free plan with an unlimited number of operators. Chat helps consult clients, collect leads, and offload support, remaining fully GDPR-compliant (hosting in EC).')}
            </p>

            <div className="hero-cta">
              <button
                className="btn-primary pulse"
                onClick={onGetStarted}
              >
                {t('Start for free')}
              </button>
              <button className="btn-secondary" onClick={() => {
                document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
              }}>
                <span className="play-icon">▶</span>
                {t('See how it works')}
              </button>
              {!isAuthenticated && onBrowseTemplates && (
                <button
                  type="button"
                  className="btn-tertiary hero-templates-link"
                  onClick={onBrowseTemplates}
                >
                  {t('Choose a template')}
                </button>
              )}
            </div>

            <p className="hero-note">
              {t('No card needed. Setup in a couple of minutes — script or integration with your CMS.')}
            </p>
          </div>

          {/* Chat Widget */}
          <div
            className="chat-widget-container"
            style={{ transform: `translateY(${scrollY * 0.03}px)` }}
          >
            <div className="chat-widget">
              <div className="chat-header">
                <div className="chat-header-title">{t('Online consultant')}</div>
                <div className="chat-header-subtitle">{t('We\'ll answer in a minute')}</div>
              </div>
              
              <div className="chat-messages" ref={chatMessagesRef}>
                {chatMessages.map((message) => {
                  const isUser = message.type === 'user';
                  const isAi = message.type === 'ai';
                  const classes = `message ${isUser ? 'user' : 'bot'}${isAi ? ' ai' : ''}`;
                  return (
                    <div key={message.id} className={classes}>
                      <div className="message-bubble">
                        <div className="message-text">{message.text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="features-section fade-in-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('Supchats Features')}</h2>
            <p className="section-description">
              {t('Supchats combines live operator communication with the power of neural networks: AI suggests answers, works with ready-made scenarios, helps close requests faster and not lose a single lead.')}
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card slide-in-section" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <button
                  className="feature-cta"
                  onClick={onGetStarted}
                >
                  {t('Start free now')}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="comparison-section fade-in-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('Supchats agent vs operator')}</h2>
            <p className="section-description">
              {t('Compare the advantages of an AI agent and the traditional approach with live operators')}
            </p>
          </div>
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-feature-col">{t('Feature')}</div>
              <div className="comparison-ai-col">
                {t('AI Agent')}
                <span className="comparison-badge">{t('Recommended')}</span>
              </div>
              <div className="comparison-operator-col">{t('Live Operator')}</div>
            </div>
            {comparisonItems.map((item, index) => (
              <div key={index} className="comparison-row slide-in-section" style={{ animationDelay: `${index * 0.05}s` }}>
                <div className="comparison-feature-col">
                  <div className="comparison-feature">{item.feature}</div>
                </div>
                <div className="comparison-ai-col">
                  <div className="comparison-text positive">{item.ai}</div>
                </div>
                <div className="comparison-operator-col">
                  <div className="comparison-text negative">{item.operator}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section fade-in-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">{t('How to use Supchats in your business')}</h2>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card slide-in-section" style={{ animationDelay: `${index * 0.15}s` }}>
                <h3 className="use-case-title">{useCase.title}</h3>
                <p className="use-case-description">{useCase.description}</p>
                <button
                  className="use-case-cta"
                  onClick={onGetStarted}
                >
                  {useCase.cta} →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section fade-in-section">
        <div className="section-container">
          <h2 className="final-cta-title">{t('Ready to start? Connect Supchats for free')}</h2>
          <button
            className="btn-primary large pulse"
            onClick={onGetStarted}
          >
            {t('Start for free')} →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="logo">
                <img
                  src={process.env.PUBLIC_URL + '/logo512.png'}
                  alt="Supchats logo"
                  className="logo-img"
                />
                <span className="logo-text">Supchats</span>
              </div>
              <p className="footer-description">
                {t('Empowering businesses with intelligent AI customer support solutions.')}
              </p>
            </div>
            <div className="footer-links">
              <div className="link-group">
                <h4>{t('Product')}</h4>
                <a href="#features" onClick={(e) => { e.preventDefault(); document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' }); }}>
                  {t('Features')}
                </a>
              </div>
              <div className="link-group">
                <h4>{t('Company')}</h4>
                <a
                  href="/contact"
                  onClick={(e) => {
                    e.preventDefault();
                    try { window.location.href = '/contact'; } catch (_) {}
                  }}
                >
                  {t('Contact Us')}
                </a>
                <a href="https://blog.supchats.com" target="_blank" rel="noreferrer">
                  {t('Blog')}
                </a>
                <a
                  href="/contacts"
                  onClick={(e) => {
                    e.preventDefault();
                    try { window.location.href = '/contacts'; } catch (_) {}
                  }}
                >
                  {t('Company and team details')}
                </a>
                <a
                  href="/privacy"
                  onClick={(e) => {
                    e.preventDefault();
                    try { window.location.href = '/privacy'; } catch(_) {}
                  }}
                >
                  {t('Privacy Policy')}
                </a>
                <a
                  href="/terms"
                  onClick={(e) => {
                    e.preventDefault();
                    try { window.location.href = '/terms'; } catch(_) {}
                  }}
                >
                  {t('Terms and Conditions')}
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>BigdealAgency © 2015-2025</p>
            <p>{t('TecophoneLTD: Kypranoros, 13 EVI BUILDING, 2nd floor, office 201, 1061, Nicosia, Cyprus')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageV2;

